import { useCallback } from 'react';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import type {
  GameProgress,
  RiverRushProgress,
  RescueMissionProgress,
  RescuedOtterCard
} from '../types';
import { defaultGameProgress } from '../types';

export function useGameProgress() {
  const [progress, setProgress] = useLocalStorage<GameProgress>('otter-games-progress', defaultGameProgress);

  // River Rush methods
  const updateRiverRushScore = useCallback((score: number, distance: number, foodCollected: number) => {
    setProgress((prev) => ({
      ...prev,
      riverRush: {
        ...prev.riverRush,
        highScore: Math.max(prev.riverRush.highScore, score),
        totalDistance: prev.riverRush.totalDistance + distance,
        totalFoodCollected: prev.riverRush.totalFoodCollected + foodCollected,
        gamesPlayed: prev.riverRush.gamesPlayed + 1,
        fishTokens: prev.riverRush.fishTokens + Math.floor(score / 100),
      },
    }));
  }, [setProgress]);

  const unlockOtter = useCallback((otterId: string) => {
    setProgress((prev) => {
      if (prev.riverRush.unlockedOtters.includes(otterId)) {
        return prev;
      }
      return {
        ...prev,
        riverRush: {
          ...prev.riverRush,
          unlockedOtters: [...prev.riverRush.unlockedOtters, otterId],
        },
      };
    });
  }, [setProgress]);

  const purchaseUpgrade = useCallback((
    upgrade: keyof RiverRushProgress['upgrades'],
    cost: number
  ): boolean => {
    let success = false;
    setProgress((prev) => {
      if (prev.riverRush.fishTokens < cost) {
        return prev;
      }
      const maxLevel = upgrade === 'thickerFur' ? 2 : 3;
      if (prev.riverRush.upgrades[upgrade] >= maxLevel) {
        return prev;
      }
      success = true;
      return {
        ...prev,
        riverRush: {
          ...prev.riverRush,
          fishTokens: prev.riverRush.fishTokens - cost,
          upgrades: {
            ...prev.riverRush.upgrades,
            [upgrade]: prev.riverRush.upgrades[upgrade] + 1,
          },
        },
      };
    });
    return success;
  }, [setProgress]);

  // Rescue Mission methods
  const addRescuedOtter = useCallback((card: RescuedOtterCard, starsEarned: number) => {
    setProgress((prev) => {
      const newTotalRescues = prev.rescueMission.totalRescues + 1;
      let newRank = prev.rescueMission.rescuerRank;

      // Update rank based on total rescues
      if (newTotalRescues >= 51) newRank = 'conservation-hero';
      else if (newTotalRescues >= 31) newRank = 'rehabilitation-expert';
      else if (newTotalRescues >= 16) newRank = 'senior-keeper';
      else if (newTotalRescues >= 6) newRank = 'junior-keeper';

      return {
        ...prev,
        rescueMission: {
          ...prev.rescueMission,
          rescuedOtters: [...prev.rescueMission.rescuedOtters, card],
          totalRescues: newTotalRescues,
          rescuerRank: newRank,
          stars: prev.rescueMission.stars + starsEarned,
        },
      };
    });
  }, [setProgress]);

  const purchaseFacilityUpgrade = useCallback((
    facility: keyof RescueMissionProgress['facilityUpgrades'],
    cost: number
  ): boolean => {
    let success = false;
    setProgress((prev) => {
      if (prev.rescueMission.stars < cost) {
        return prev;
      }
      if (prev.rescueMission.facilityUpgrades[facility] >= 3) {
        return prev;
      }
      success = true;
      return {
        ...prev,
        rescueMission: {
          ...prev.rescueMission,
          stars: prev.rescueMission.stars - cost,
          facilityUpgrades: {
            ...prev.rescueMission.facilityUpgrades,
            [facility]: prev.rescueMission.facilityUpgrades[facility] + 1,
          },
        },
      };
    });
    return success;
  }, [setProgress]);

  const addStars = useCallback((amount: number) => {
    setProgress((prev) => ({
      ...prev,
      rescueMission: {
        ...prev.rescueMission,
        stars: prev.rescueMission.stars + amount,
      },
    }));
  }, [setProgress]);

  const resetGameProgress = useCallback(() => {
    setProgress(defaultGameProgress);
  }, [setProgress]);

  return {
    progress,
    // River Rush
    updateRiverRushScore,
    unlockOtter,
    purchaseUpgrade,
    // Rescue Mission
    addRescuedOtter,
    purchaseFacilityUpgrade,
    addStars,
    // General
    resetGameProgress,
  };
}
